// lg-mega-menu.js — controller for the <lg-mega-menu> navigation (desktop + mobile).
//
// Scope (ADR-0010 amended / ADR-0019): ONE block tree, two presentations (§3.13).
//   • Desktop (>=901, non-touch): drop panels (S4c+) under the nav row.
//   • Mobile / touch: a top-layer <dialog> drawer whose body IS the same nav,
//     adopted by a one-time move (shape ii — headless-verified 2026-07-26).
//
// Desktop panel behavior (active from S4c, when triggers gain panels):
//   • Each trigger with a panel carries [data-mm]; its panel is a [data-panel]
//     sibling positioned absolutely under the section-header.
//   • Hover-intent open (150ms) + close delay (150ms); hovering the panel keeps it.
//   • Click / keyboard toggle parity; single panel open; ESC / outside / scrim close.
//   • aria-expanded synced per trigger; `hidden` mirrored for a11y / no-JS.
//
// Mobile drawer behavior (S4b):
//   • In drawer mode (narrow viewport OR touch) the controller moves <nav.lg-mm__nav>
//     into the <dialog> once (idempotent; moved back on crossing to desktop).
//   • Burger -> <dialog-component>.showDialog() (native showModal, top layer — §3.11);
//     Horizon's DialogComponent owns scroll-lock / Esc / outside-click (§4.2).
//   • Close (× / Esc / crossing to desktop) restores focus to the burger.
//
// With no panels AND no drawer wiring present (pre-S4b DOM), the element registers
// and every branch below no-ops cleanly.

const HOVER_OPEN_DELAY = 150;
const HOVER_CLOSE_DELAY = 150;
// LG drawer boundary 900/901 (F2) — keep in step with lg-mega-menu.css / lg-header.css
// (checks.sh header-breakpoint-constants gate). Horizon's own 749/750 is unchanged.
const DRAWER_MQ = '(max-width: 900.98px)';

class LgMegaMenu extends HTMLElement {
  constructor() {
    super();
    this._openTimer = null;
    this._closeTimer = null;
    this._activePanel = null;
    this._lastTrigger = null;
    this._scrim = null;
    this._onDocClick = this._onDocClick.bind(this);
    this._onDocKey = this._onDocKey.bind(this);
    this._onFocusOut = this._onFocusOut.bind(this);
    this._onOverlayOpen = this._onOverlayOpen.bind(this);
    this._syncDrawerHome = this._syncDrawerHome.bind(this);
    this._onDialogOpen = this._onDialogOpen.bind(this);
    this._onDialogClose = this._onDialogClose.bind(this);
  }

  connectedCallback() {
    this._triggers = Array.from(this.querySelectorAll('[data-mm]'));
    this._panels = Array.from(this.querySelectorAll('[data-panel]'));

    this._wirePanels();
    this._wireDrawer();

    document.addEventListener('click', this._onDocClick);
    document.addEventListener('keydown', this._onDocKey);
    // Mutual exclusion with the search dropdown (both are header overlays): when the
    // search panel opens it emits `lg:overlay-open`, and we close any open panel/drawer
    // so the two never stack. We emit the same on our open so the search closes too.
    document.addEventListener('lg:overlay-open', this._onOverlayOpen);
    // Keyboard focus leaving the menu closes any open desktop panel, so the
    // scrim never sits over the now-focused page content (WCAG 2.4.3). Scoped
    // to the host so it only fires for focus moving out of the nav subtree.
    this.addEventListener('focusout', this._onFocusOut);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this._onDocClick);
    document.removeEventListener('keydown', this._onDocKey);
    document.removeEventListener('lg:overlay-open', this._onOverlayOpen);
    this.removeEventListener('focusout', this._onFocusOut);
    this._clearOpenTimer();
    this._clearCloseTimer();
    if (this._scrim && this._scrim.parentNode === document.body) {
      document.body.removeChild(this._scrim);
    }
    if (this._mql) this._mql.removeEventListener('change', this._syncDrawerHome);
    if (this._menuStyleObserver) this._menuStyleObserver.disconnect();
  }

  // Each trigger has a [data-panel] sibling. CSS stacks them all at the same
  // absolute spot under the section-header; JS toggles `.is-active` on one.
  _wirePanels() {
    if (this._triggers.length === 0) return;
    const supportsHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

    this._triggers.forEach((trigger) => {
      const panelId = trigger.getAttribute('data-mm');
      const panel = this._panels.find((p) => p.getAttribute('data-panel') === panelId);
      // Triggers without a panel (e.g. Blog) stay plain links.
      if (!panel) return;

      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        this._lastTrigger = trigger;
        if (this._activePanel === panelId) this._closePanel();
        else this._openPanel(panelId);
      });

      // Space parity for the <a>-variant trigger. A <button> fires `click` on
      // both Enter and Space natively; an <a> fires it only on Enter, so the
      // anchor trigger (kept for no-JS / middle-click navigation) would be
      // Space-dead. Toggle on Space here and preventDefault the page-scroll.
      // Enter is left to the browser's native anchor activation -> `click`.
      if (trigger.tagName === 'A') {
        trigger.addEventListener('keydown', (e) => {
          if (e.key !== ' ' && e.key !== 'Spacebar') return;
          e.preventDefault();
          this._lastTrigger = trigger;
          if (this._activePanel === panelId) this._closePanel();
          else this._openPanel(panelId);
        });
      }

      if (supportsHover) {
        trigger.addEventListener('mouseenter', () => {
          this._lastTrigger = trigger;
          this._clearCloseTimer();
          this._scheduleOpen(panelId);
        });
        trigger.addEventListener('mouseleave', () => {
          this._clearOpenTimer();
          this._scheduleClose();
        });
        panel.addEventListener('mouseenter', () => this._clearCloseTimer());
        panel.addEventListener('mouseleave', () => this._scheduleClose());
      }
    });
  }

  // ---- Mobile drawer (§3.13 shape ii) -----------------------------------------

  _wireDrawer() {
    this._burger = this.querySelector('.lg-mm__burger');
    this._dialogHost = this.querySelector('.lg-mm__dialog-host');
    this._dialog = this._dialogHost && this._dialogHost.querySelector('dialog.lg-mm__drawer');
    this._nav = this.querySelector('.lg-mm__nav');
    this._drawerHead = this._dialog && this._dialog.querySelector('.lg-mm__drawer-head');
    this._closeBtn = this._dialog && this._dialog.querySelector('.lg-mm__drawer-close');
    this._headerEl = document.getElementById('header-component');

    // No-op cleanly if the drawer shell isn't present (pre-S4b DOM).
    if (!this._burger || !this._dialogHost || !this._dialog || !this._nav || !this._drawerHead) {
      return;
    }

    this._burger.addEventListener('click', () => this._openDrawer());
    if (this._closeBtn) this._closeBtn.addEventListener('click', () => this._closeDrawer());
    this._dialogHost.addEventListener('dialog:open', this._onDialogOpen);
    this._dialogHost.addEventListener('dialog:close', this._onDialogClose);

    // Re-evaluate the nav's home when the breakpoint (viewport) or Horizon's
    // touch signal (`data-menu-style`) changes. `data-menu-style` is JS-set after
    // load, so the observer also settles the initial touch-tablet case.
    this._mql = window.matchMedia(DRAWER_MQ);
    this._mql.addEventListener('change', this._syncDrawerHome);
    if (this._headerEl) {
      this._menuStyleObserver = new MutationObserver(this._syncDrawerHome);
      this._menuStyleObserver.observe(this._headerEl, {
        attributes: true,
        attributeFilter: ['data-menu-style'],
      });
    }

    this._syncDrawerHome();
  }

  _isDrawerMode() {
    const narrow = this._mql ? this._mql.matches : window.matchMedia(DRAWER_MQ).matches;
    const touch = this._headerEl && this._headerEl.getAttribute('data-menu-style') === 'drawer';
    return narrow || Boolean(touch);
  }

  // Keeps the nav in its correct home. While the dialog is open, defers the move to
  // the close handler so the nav is never yanked out of an open modal (and scroll-lock
  // is released by DialogComponent, not bypassed).
  _syncDrawerHome() {
    const drawer = this._isDrawerMode();
    if (!drawer && this._dialog.open) {
      this._closeDrawer(); // dialog:close -> _moveNavToRow
      return;
    }
    if (this._dialog.open) return;
    if (drawer) {
      // Crossing into drawer mode with a desktop panel open (a zoom step across 900/901 can
      // do this): close it — otherwise the scrim + data-active outlive the row — and, if focus
      // was in the nav, hand it to the burger instead of letting it fall to <body> (2.4.3).
      const hadFocus = this._nav.contains(document.activeElement);
      this._closePanel();
      this._moveNavIntoDrawer();
      this._setCategoriesOpen(false); // drawer: accordions collapsed
      if (hadFocus) this._burger.focus();
    } else {
      this._moveNavToRow();
      this._setCategoriesOpen(true); // desktop: <details> open so columns show
    }
  }

  // Category columns are server-rendered <details open> (desktop shows without JS).
  // In the drawer they are collapsible accordions; on desktop they stay open.
  _setCategoriesOpen(open) {
    this.querySelectorAll('.lg-mm-col[data-lg-mm-details]').forEach((d) => {
      d.open = open;
    });
  }

  _moveNavIntoDrawer() {
    if (this._nav.parentElement !== this._dialog) this._drawerHead.after(this._nav);
  }

  _moveNavToRow() {
    if (this._nav.parentElement === this._dialog) this.insertBefore(this._nav, this._dialogHost);
  }

  _openDrawer() {
    document.dispatchEvent(new CustomEvent('lg:overlay-open', { detail: 'mega-menu' }));
    this._moveNavIntoDrawer();
    this._setCategoriesOpen(false); // all accordions closed on each open (Plan v2)
    if (typeof this._dialogHost.showDialog === 'function') {
      this._dialogHost.showDialog();
    } else if (typeof this._dialog.showModal === 'function') {
      this._dialog.showModal(); // fallback if DialogComponent hasn't upgraded yet
    }
  }

  _closeDrawer() {
    if (typeof this._dialogHost.closeDialog === 'function') this._dialogHost.closeDialog();
    else if (this._dialog.open) this._dialog.close();
  }

  _onDialogOpen() {
    this._burger.setAttribute('aria-expanded', 'true');
  }

  _onDialogClose() {
    this._burger.setAttribute('aria-expanded', 'false');
    // Restore focus to the burger — but only if it's still visible (drawer mode).
    // A close triggered by crossing to desktop instead re-homes the nav in the row.
    if (this._isDrawerMode()) {
      this._burger.focus();
    } else {
      this._moveNavToRow();
      this._setCategoriesOpen(true); // back on desktop: re-open columns
    }
  }

  // ---- Desktop panels ----------------------------------------------------------

  _ensureScrim() {
    if (this._scrim) return;
    this._scrim = document.createElement('div');
    this._scrim.className = 'lg-mm__scrim';
    this._scrim.setAttribute('data-lg-mm-scrim', '');
    this._scrim.addEventListener('click', () => this._closePanel());
    document.body.appendChild(this._scrim);
  }

  _scheduleOpen(panelId) {
    this._clearOpenTimer();
    this._openTimer = window.setTimeout(() => this._openPanel(panelId), HOVER_OPEN_DELAY);
  }

  _scheduleClose() {
    this._clearCloseTimer();
    this._closeTimer = window.setTimeout(() => this._closePanel(), HOVER_CLOSE_DELAY);
  }

  _clearOpenTimer() {
    if (this._openTimer !== null) {
      window.clearTimeout(this._openTimer);
      this._openTimer = null;
    }
  }

  _clearCloseTimer() {
    if (this._closeTimer !== null) {
      window.clearTimeout(this._closeTimer);
      this._closeTimer = null;
    }
  }

  _openPanel(panelId) {
    this._clearOpenTimer();
    this._clearCloseTimer();
    // Close the search dropdown (and any other header overlay) — mutual exclusion.
    document.dispatchEvent(new CustomEvent('lg:overlay-open', { detail: 'mega-menu' }));
    this._ensureScrim();
    this._activePanel = panelId;
    this.setAttribute('data-active', panelId);

    this._panels.forEach((p) => {
      const isActive = p.getAttribute('data-panel') === panelId;
      p.classList.toggle('is-active', isActive);
      if (isActive) p.removeAttribute('hidden');
      else p.setAttribute('hidden', '');
    });
    this._triggers.forEach((t) => {
      t.setAttribute('aria-expanded', t.getAttribute('data-mm') === panelId ? 'true' : 'false');
    });
    if (this._scrim) this._scrim.classList.add('is-open');
  }

  _closePanel() {
    this._clearOpenTimer();
    this._clearCloseTimer();
    if (this._activePanel === null) return;
    this._activePanel = null;
    this.removeAttribute('data-active');
    if (this._scrim) this._scrim.classList.remove('is-open');
    this._panels.forEach((p) => {
      p.classList.remove('is-active');
      p.setAttribute('hidden', '');
    });
    this._triggers.forEach((t) => t.setAttribute('aria-expanded', 'false'));
  }

  _onDocClick(e) {
    if (this._activePanel === null) return;
    if (this.contains(e.target)) return;
    if (this._scrim && this._scrim.contains(e.target)) return;
    this._closePanel();
  }

  _onDocKey(e) {
    if (e.key !== 'Escape' || this._activePanel === null) return;
    const target = this._lastTrigger || this._triggers[0];
    this._closePanel();
    if (target && typeof target.focus === 'function') target.focus();
  }

  // Close the open panel when keyboard focus leaves the whole menu subtree.
  // `relatedTarget` is where focus is going next: if it's still inside the host
  // (moving between a trigger and its panel, or to another trigger) we keep the
  // panel; if it's outside, we close. `relatedTarget === null` means focus left
  // the document entirely (e.g. to the address bar / another window) — don't
  // thrash the panel closed in that case; ESC / outside-click / blur handle it.
  _onFocusOut(e) {
    if (this._activePanel === null) return;
    const next = e.relatedTarget;
    if (next === null) return;
    if (this.contains(next)) return;
    this._closePanel();
  }

  // Another header overlay (the search dropdown) opened → close our panel/drawer so the
  // two never stack (mutual exclusion). Ignore our own event.
  _onOverlayOpen(e) {
    if (e.detail === 'mega-menu') return;
    this._closePanel();
    if (this._dialog && this._dialog.open) this._closeDrawer();
  }
}

if (!customElements.get('lg-mega-menu')) {
  customElements.define('lg-mega-menu', LgMegaMenu);
}
